import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import DashboardLayout from '../components/layout/DashboardLayout';
import Container from '../components/layout/Container';
import FormInput from '../components/tickets/form/FormInput';
import { userService } from '../services/userService';
import { AlertCircle } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

type ChangePasswordFormData = {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
};

export default function ChangePasswordPage() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  
  // Define schema inside component where t is available
  const changePasswordSchema = z.object({
    currentPassword: z.string().min(1, t('pages.changePassword.validation.currentRequired')),
    newPassword: z.string()
      .min(6, t('pages.changePassword.validation.minLength'))
      .max(50, t('pages.changePassword.validation.maxLength')),
    confirmPassword: z.string().min(1, t('pages.changePassword.validation.confirmRequired')),
  }).refine((data) => data.newPassword === data.confirmPassword, {
    message: t('pages.changePassword.validation.passwordsDontMatch'),
    path: ["confirmPassword"],
  });

  const { register, handleSubmit, formState: { errors }, reset } = useForm<ChangePasswordFormData>({
    resolver: zodResolver(changePasswordSchema)
  });

  const onSubmit = async (data: ChangePasswordFormData) => {
    setIsSubmitting(true);
    setError(null);
    try {
      await userService.changePassword({
        currentPassword: data.currentPassword,
        newPassword: data.newPassword
      });
      setSuccess(true);
      reset();
      // Navigate back after successful password change
      setTimeout(() => {
        navigate('/tickets');
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('pages.changePassword.failedToChange'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <DashboardLayout>
      <Container>
        <div className="max-w-md mx-auto mt-8">
          <h1 className={`text-2xl font-bold mb-6 ${
            theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
          }`}>
            {t('pages.changePassword.title')}
          </h1>
          
          {error && (
            <div className={`mb-4 p-4 rounded-md flex items-start space-x-2 ${
              theme === 'dark'
                ? 'bg-red-900/30 border-red-800/50 text-red-200'
                : 'bg-red-50 border border-red-200 text-red-800'
            }`}>
              <AlertCircle className={`h-5 w-5 ${
                theme === 'dark' ? 'text-red-400' : 'text-red-500'
              } mt-0.5`} />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {success && (
            <div className={`mb-4 p-4 rounded-md ${
              theme === 'dark'
                ? 'bg-green-900/30 border border-green-800/50 text-green-200'
                : 'bg-green-50 border border-green-200 text-green-800'
            }`}>
              <span className="text-sm">{t('pages.changePassword.success')}</span>
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <FormInput
              label={t('pages.changePassword.currentPassword')}
              name="currentPassword"
              type="password"
              required
              register={register}
              error={errors.currentPassword?.message}
              disabled={isSubmitting}
            />

            <FormInput
              label={t('pages.changePassword.newPassword')}
              name="newPassword"
              type="password"
              required
              register={register}
              error={errors.newPassword?.message}
              disabled={isSubmitting}
            />

            <FormInput
              label={t('pages.changePassword.confirmPassword')}
              name="confirmPassword"
              type="password"
              required
              register={register}
              error={errors.confirmPassword?.message}
              disabled={isSubmitting}
            />

            <div className="flex items-center justify-end space-x-4">
              <button
                type="button"
                onClick={() => navigate('/tickets')}
                className={`px-4 py-2 text-sm font-medium ${
                  theme === 'dark'
                    ? 'text-gray-300 hover:text-gray-100'
                    : 'text-gray-700 hover:text-gray-500'
                }`}
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className={`inline-flex justify-center rounded-md border border-transparent py-2 px-4 text-sm font-medium text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                  theme === 'dark'
                    ? 'bg-blue-600 hover:bg-blue-500 focus:ring-blue-400 focus:ring-offset-gray-800'
                    : 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
                } ${isSubmitting ? 'opacity-75 cursor-not-allowed' : ''}`}
              >
                {isSubmitting ? t('pages.changePassword.changing') : t('pages.changePassword.submit')}
              </button>
            </div>
          </form>
        </div>
      </Container>
    </DashboardLayout>
  );
}
